<template>
  <div class="eu-pt_5 eu-pb_4">
    <emd-container :title="$t('linkExpiration')">
      <template v-slot:content>
        <div class="eu-flex">
          <div class="eu-flex-item eu-basis_6 eu-basis_6_md">
            <div class="emd-form-group">
              <label class="emd-label">{{ $t('expirationTime') }}</label>
              <div class="emd-select">
                <select v-model="selectedExpiresIn">
                  <option v-for="([, { label }], index) in intervals"
                    :key="`${label}-${index}`"
                    :value="label"
                  >
                    {{ $t(`interval.${label.toLowerCase()}`) }}
                  </option>
                </select>
                <div class="emd-select__arrow"></div>
              </div>
            </div>
            <div id="customIntervalsSelect" v-if="isCustomSelected">
              <div class="emd-form-group" >
                <label class="emd-label">{{ $t('expirationPeriod') }}</label>
                <div class="emd-select">
                  <select
                    v-model="expiresInPeriod"
                    :class="{'_state_error': $v.expiresInPeriod.$invalid}"
                  >
                    <option
                      v-for="([, { label, }], index) in customIntervals"
                      :key="`${label}-${index}`"
                      :value="label"
                    >
                      {{ $t(`customDate.${label.toLowerCase()}`) }}
                    </option>
                  </select>
                  <div class="emd-select__arrow"></div>
                </div>
              </div>
            </div>
          </div>
          <div class="eu-flex-item eu-basis_6 eu-basis_6_md">
            <div class="emd-form-group">
              <label class="emd-label">
                {{ $t('paymentsLimit') }}
                <emd-tippy
                  id="company"
                  target="_blank"
                  rel="noopener noreferrer"
                  tag="a"
                  class="_has-tooltip"
                  :type="'action-button'"
                  :position="'top'"
                  :content="$t('expirationTooltip')"
                >
                  <i class="uil uil-info-circle emd-text _color_success"/>
                </emd-tippy>
              </label>
              <div class="eu-flex eu-align-items_center">
                <div class="eu-flex-item">
                  <div class="emd-input-group _icon_left _icon_right">
                    <a class="emd-input-icon__link" @click="subtractQuantity()">
                      <i class="uil uil-minus"></i>
                    </a>
                    <input
                      disabled
                      class="emd-input eu-align_center"
                      type="text"
                      :value="paymentLinkData.max_paid_sessions"
                      @input="changeValue($event, 'max_paid_sessions')">
                    <a class="emd-input-icon__link" @click="addQuantity()">
                      <i class="uil uil-plus"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div id="customIntervalsInput" v-if="isCustomSelected">
              <div class="emd-form-group">
                <label class="emd-label">{{ $t('expirationValue') }}</label>
                <div class="emd-select">
                  <input
                    v-model.number="expiresInValue"
                    :class="{'_state_error': $v.expiresInValue.$invalid}"
                    class="emd-input"
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="eu-flex">
          <div class="eu-flex-item eu-basis_12 eu-basis_6_md">
            <div class="emd-form-group eu-py_2">
              <label class="emd-checkbox">
                <input
                  :disabled="!isOneSession"
                  type="checkbox"
                  class="emd-checkbox__input"
                  v-model="paymentLinkData.customerInformation"
                  @change="changeCustomerInformation"
                />
                <div class="emd-checkbox__area"></div>
                <span class="emd-checkbox__text">
                  {{ $t('customerInformation') }}
                </span>
              </label>
            </div>
          </div>
        </div>
        <div class="eu-flex" v-if="paymentLinkData.customerInformation">
          <div class="eu-flex-item eu-basis_12 eu-basis_6_md">
            <div class="emd-form-group">
              <label class="emd-label">{{ $t('personName') }}</label>
              <input
                :class="{'_state_error': $v.customerData.name.$invalid && showFeedbacks.status }"
                :value="customerData.name"
                @input="changeCustomerData($event, 'name')"
                class="emd-input"
                placeholder="Ex.: João da Silva"
              />
              <p class="emd-input-feedback _state_error" v-if="!$v.customerData.name.maxLength && showFeedbacks.status">
                {{ $t('hint.length', { length: '120' }) }}
              </p>
              <p class="emd-input-feedback _state_error" v-if="!$v.customerData.name.minLength && showFeedbacks.status">
                {{ $t('hint.minLength', { length: '2' }) }}
              </p>
              <p class="emd-input-feedback _state_error" v-if="!$v.customerData.name.required && showFeedbacks.status">
                {{ $t('hint.filledField') }}
              </p>
            </div>
          </div>
          <div class="eu-flex-item eu-basis_12 eu-basis_6_md">
            <div class="emd-form-group">
              <label class="emd-label">{{ $t('email') }}</label>
              <input
                :class="{'_state_error': $v.customerData.email.$invalid && showFeedbacks.status}"
                :value="customerData.email"
                @input="changeCustomerData($event, 'email')"
                class="emd-input"
                placeholder="exemplo@exemplo.com"
              />
              <p class="emd-input-feedback _state_error" v-if="!$v.customerData.email.email && showFeedbacks.status">
                {{ $t('hint.email') }}
              </p>
              <p class="emd-input-feedback _state_error" v-if="!$v.customerData.email.required && showFeedbacks.status">
                {{ $t('hint.filledField') }}
              </p>
            </div>
          </div>
        </div>
        <emd-collapse :title="$t('address')" variant="subcontainer" v-if="showAddressFormSection">
          <template #content>
            <address-form :address="address" @toggle="toggleAddressFormState" @addressIsLoading="toggleLoadingAddress"/>
          </template>
        </emd-collapse>
      </template>
    </emd-container>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { INTERVAL_ENUM, EXPIRES_IN_PERIOD } from '../../../enums/expiration.enum'
import { minLength, maxLength, email, requiredIf } from 'vuelidate/lib/validators'
import { EmdCollapse } from 'emerald-vue/src/components'
import EmdContainer from '@/components/emerald/molecules/EmdContainer.vue'
import AddressForm from '@/components/project/AddressForm'
import EmdTippy from '@/components/emerald/atoms/EmdTippy'

export default {
  name: 'LinkExpiration',
  components: {
    EmdContainer,
    AddressForm,
    EmdTippy,
    EmdCollapse
  },
  data () {
    return {
      addressFormIsInvalid: true,
      loadingAddress: false,
      selectedExpiresIn: INTERVAL_ENUM.NONE.label,
      expiresInValue: 1,
      expiresInPeriod: EXPIRES_IN_PERIOD.DAYS.label
    }
  },
  computed: {
    ...mapGetters(['paymentLinkData', 'customerData', 'showFeedbacks', 'address']),
    intervals () {
      return Object.entries(INTERVAL_ENUM)
    },
    customIntervals () {
      return Object.entries(EXPIRES_IN_PERIOD)
    },
    isOneSession () {
      return Number(this.paymentLinkData.max_paid_sessions) === 1
    },
    showAddressFormSection () {
      return this.paymentLinkData.customerInformation
    },
    isCustomSelected () {
      return this.selectedExpiresIn === INTERVAL_ENUM.CUSTOM.label
    },
    selectedExpiresInValue () {
      return INTERVAL_ENUM[this.selectedExpiresIn?.toUpperCase()]?.value || 0
    },
    selectedExpiresInPeriodValue () {
      return EXPIRES_IN_PERIOD[this.expiresInPeriod?.toUpperCase()]?.value || 0
    },
    totalPeriod () {
      return this.selectedExpiresInPeriodValue * this.expiresInValue
    }
  },
  validations: {
    expiresInValue: {
      required: requiredIf(function () { return !!this.isCustomSelected })
    },
    expiresInPeriod: {
      required: requiredIf(function () { return !!this.isCustomSelected })
    },
    customerData: {
      name: {
        minLength: minLength(2),
        maxLength: maxLength(120),
        required: requiredIf(function () { return this.paymentLinkData.customerInformation && !this.customerData.email })
      },
      email: {
        email,
        required: requiredIf(function () { return this.paymentLinkData.customerInformation && !this.customerData.name })
      }
    }
  },
  watch: {
    selectedExpiresIn () {
      this.updateExpiresIn()
    },
    expiresInValue () {
      const expiresIn = Number(this.expiresInValue.toString().replace(/\D/g, ''))
      this.expiresInValue = Math.min(expiresIn > 0 ? expiresIn : 1, 100)
      this.updateExpiresIn()
    },
    expiresInPeriod () {
      this.updateExpiresIn()
    },
    'customerData.name' (value) {
      this.customerData.name = value.replace(/[^a-zA-Z\s]/g, '')
    }
  },
  mounted () {
    this.selectedExpiresIn = this.paymentLinkData.selectedExpiresIn ||
      INTERVAL_ENUM.NONE.label

    this.expiresInPeriod = this.paymentLinkData.expiresInPeriod ||
      EXPIRES_IN_PERIOD.DAYS.label

    this.expiresInValue = this.paymentLinkData.expiresInValue || 1
    this.updateExpiresIn()
  },
  created () {
    if (!this.paymentLinkData.expiresIn) {
      this.$store.dispatch('UPDATE_DATA', {
        value: INTERVAL_ENUM.NONE.label,
        attribute: 'expiresIn'
      })
    }

    if (!this.paymentLinkData.max_paid_sessions) {
      this.$store.dispatch('UPDATE_DATA',
        {
          value: this.$t('noLimit'),
          attribute: 'max_paid_sessions'
        }
      )
    }
  },
  methods: {
    ...mapActions({ updateData: 'UPDATE_DATA' }),
    updateExpiresIn () {
      this.$emit('formIsValid', this.$v.$invalid)

      const value = this.isCustomSelected
        ? this.totalPeriod
        : this.selectedExpiresInValue

      this.updateData({
        value,
        attribute: 'expiresIn'
      })

      this.updateData({
        value: this.selectedExpiresIn,
        attribute: 'selectedExpiresIn'
      })

      this.updateData({
        value: this.expiresInValue,
        attribute: 'expiresInValue'
      })

      this.updateData({
        value: this.expiresInPeriod,
        attribute: 'expiresInPeriod'
      })

      this.maxSessionsInput()
    },
    changeCustomerInformation () {
      this.$emit('formIsValid', this.$v.$invalid)
    },
    changeCustomerData (event, attribute) {
      this.$store.dispatch('UPDATE_CUSTOMER_DATA', {
        value: event.target?.value,
        attribute
      })
      this.$emit('formIsValid', this.$v.$invalid)
    },
    maxSessionsInput () {
      if (!this.isOneSession) {
        this.paymentLinkData.customerInformation = false
      }
    },
    subtractQuantity () {
      this.paymentLinkData.customerInformation = false
      let maxSession = null
      if (this.paymentLinkData.max_paid_sessions !== this.$t('noLimit') && !this.isOneSession) {
        maxSession = Number(this.paymentLinkData.max_paid_sessions) - 1
        this.$store.dispatch('UPDATE_DATA',
          {
            value: maxSession,
            attribute: 'max_paid_sessions'
          }
        )
      } else if (this.isOneSession) {
        this.$store.dispatch('UPDATE_DATA',
          {
            value: this.$t('noLimit'),
            attribute: 'max_paid_sessions'
          }
        )
      }
    },
    addQuantity () {
      this.paymentLinkData.customerInformation = false
      let maxSession = null
      if (this.paymentLinkData.max_paid_sessions !== this.$t('noLimit')) {
        maxSession = Number(this.paymentLinkData.max_paid_sessions) + 1
      } else {
        maxSession = 1
      }

      this.$store.dispatch('UPDATE_DATA',
        {
          value: maxSession,
          attribute: 'max_paid_sessions'
        }
      )
    },
    toggleAddressFormState (state) {
      this.addressFormIsInvalid = state
    },
    toggleLoadingAddress (state) {
      this.loadingAddress = state
    }
  }
}
</script>

<style scoped>
.emd-input:disabled {
  background-color: #fff !important;
  color: #303438 !important;
  cursor: auto !important;
}

.emd-select ._state_error {
  border-color: var(--brand--le--danger-color--base, #df285f)
}

._mode_dark .emd-input:disabled {
  background-color: transparent !important;
  color: #c4c6c8 !important;
  cursor: auto !important;
}
</style>

<i18n>
{
  "pt-BR": {
    "linkExpiration": "Expiração do link",
    "optional": "opcional",
    "email": "E-mail",
    "customerInformation": "Especificar quem vai pagar",
    "paymentsLimit": "Limite de pagamentos",
    "expirationTime": "Tempo de expiração",
    "expirationPeriod": "Período de expiração",
    "expirationValue": "Valor de expiração",
    "personName": "Nome da pessoa",
    "interval": {
      "custom": "Personalizado",
      "none": "Não expira",
      "one_hour": "1 hora",
      "twelve_hours": "12 horas",
      "one_day": "1 dia",
      "one_hour": "1 hora",
      "one_week": "1 semana",
      "fifteen_days": "15 dias",
      "one_month": "1 mês"
    },
    "customDate": {
      "label": "Data personalizada",
      "years": "Anos",
      "months": "Meses",
      "weeks": "Semanas",
      "days": "Dias",
      "hours": "Horas",
      "minutes": "Minutos"
    },
    "noLimit": "Sem limite",
    "expirationTooltip": "Quantidade de vezes que o link pode ser pago. Ao atingir o limite, não é possível receber mais pagamentos.",
    "hint": {
      "length": "Máximo de {length} caracteres",
      "minLength": "Digite, no mínimo, {length} caracteres",
      "email": "E-mail inválido",
      "filledField": "Ao menos um campo precisa ser preenchido"
    },
    "address": "Endereço (opcional)"
  },
  "en-US": {
    "linkExpiration": "Link expiration",
    "optional": "optional",
    "email": "E-mail",
    "customerInformation": "Specify who will pay",
    "paymentsLimit": "Payments limit",
    "expirationTime": "Expiration time",
    "expirationPeriod": "Expiration period",
    "expirationValue": "Expiration value",
    "personName": "Person name",
    "interval": {
      "custom": "Custom",
      "none": "Doesn't expire",
      "one_hour": "1 hour",
      "twelveHours": "12 hours",
      "oneDay": "1 day",
      "one_hour": "1 hour",
      "one_week": "1 week",
      "fifteenDays": "15 days",
      "oneMonth": "1 month",
      "noLimit": "No limit"
    },
    "customDate": {
      "label": "Custom date",
      "years": "Years",
      "months": "Months",
      "weeks": "Weeks",
      "days": "Days",
      "hours": "Hours",
      "minutes": "Minutes"
    },
    "noLimit": "No limit",
    "expirationTooltip": "Number of times the link can be paid. Once you reach the limit, you can no longer receive payments.",
    "hint": {
      "length": "This field cannot be more than {length} characters",
      "minLength": "Please enter at least {length} characters",
      "email": "Invalid e-mail",
      "filledField": "At least one field needs to be filled"
    },
    "address": "Address (optional)"
  }
}
</i18n>
